/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ShowMoreGridSection, ShowMoreGridSectionPlaceholder } from '@coursera-web/cds-labs';

import {
  Button,
  ContextualHelp,
  Grid,
  PageGridContainer,
  Skeleton,
  Tag,
  Typography2,
  VisuallyHidden,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { HomeLevelSetCard } from 'bundles/levelsets/levelsets-v2/components/HomeLevelSetCard';
import type { LevelSetCardItem } from 'bundles/levelsets/levelsets-v2/types';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';

import _t from 'i18n!nls/consumer-home';

const SHOW_MORE_AMOUNT = 6;

const styles = {
  betaTag: css`
    color: var(--cds-color-neutral-primary-invert);
    margin-bottom: var(--cds-spacing-200);
  `,
  loadingPlaceholderCard: css`
    border-radius: 16px;
  `,
  title: css`
    margin-right: var(--cds-spacing-100);
  `,
  showMoreGridSection: css`
    padding-top: var(--cds-spacing-150);
  `,
};

type Props = {
  levelSets: LevelSetCardItem[];
  levelSetsLoading?: boolean;
  hasError: boolean;
};

const EnterpriseLevelSetsModule = ({ levelSets, levelSetsLoading, hasError }: Props) => {
  const track = useTracker();
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const { currentLevel } = useHeaderLevel();

  if (!levelSets || hasError) {
    return null;
  }

  return (
    <PageGridContainer>
      <Grid item xs={12}>
        <ContextualHelp
          defaultExpanded={false}
          helpToggleProps={{
            'aria-label': _t('Information about skill diagnostics'),
          }}
          tooltipProps={{
            closeButtonProps: {
              'aria-label': _t('Close information about skill diagnostics'),
            },
          }}
          label={
            <>
              <Typography2 variant="subtitleLarge" component={currentLevel} css={styles.title}>
                {_t('Evaluate your skills')}
              </Typography2>
              <Tag color="default" priority="primary" variant="status" css={styles.betaTag}>
                {_t('BETA')}
              </Tag>
            </>
          }
        >
          <Typography2 variant="bodyPrimary" component="p">
            {_t('Complete a skill diagnostic to gain insights into your abilities and explore relevant content.')}
          </Typography2>
        </ContextualHelp>

        <VisuallyHidden role="status">{levelSetsLoading ? _t('Loading cards') : _t('Loaded cards')}</VisuallyHidden>
        {levelSetsLoading ? (
          <ShowMoreGridSectionPlaceholder
            css={styles.showMoreGridSection}
            hideTitle
            itemsGridConfig={{ xs: 12, sm: 6, md: 4, lg: 4 }}
            defaultRows={isMobile ? 2 : 1}
            renderPlaceholderItem={() => <Skeleton css={styles.loadingPlaceholderCard} variant="rect" height={148} />}
          />
        ) : (
          <ShowMoreGridSection<LevelSetCardItem>
            css={styles.showMoreGridSection}
            items={levelSets}
            itemsGridConfig={{ xs: 12, sm: 6, md: 4, lg: 4 }}
            defaultRows={isMobile ? 2 : 1}
            spacing={{ xs: 8, sm: 24 }}
            showMoreAmount={SHOW_MORE_AMOUNT}
            renderItem={({ item, itemIndex }) => (
              <HomeLevelSetCard
                skill={item.skill}
                masteryLevel={item.masteryLevel}
                masteryPercent={item.masteryPercent}
                index={itemIndex}
              />
            )}
            onExpandButtonClick={({ numberOfItemsDisplayed }) => {
              // This fires just before the expansion or collapse actually happens
              const isCollapseButton = numberOfItemsDisplayed === levelSets.length;
              const numItemsDisplayedExcludingInitial = Math.max(0, numberOfItemsDisplayed - 2);
              const totalNumItemsExcludingInitial = Math.max(0, levelSets.length - 2);
              const pageNumBeforeClick = Math.ceil(numItemsDisplayedExcludingInitial / SHOW_MORE_AMOUNT);
              const nextPageNum = isCollapseButton ? 0 : pageNumBeforeClick + 1;
              const totalPages = Math.ceil(totalNumItemsExcludingInitial / SHOW_MORE_AMOUNT) + 1;
              track(isCollapseButton ? 'collapse_carousel' : 'expand_carousel', {
                carousel: {
                  id: 'levelsets_cards',
                  pagination: {
                    pageNumber: nextPageNum,
                    pageSize: SHOW_MORE_AMOUNT,
                    totalItems: levelSets.length,
                    totalPages,
                  },
                },
              });
            }}
            renderExpandButton={({ onClick, defaultLabel, isExpanded, showMoreCount, ...rest }) => {
              return (
                <Button
                  aria-label={defaultLabel}
                  size="small"
                  variant="secondary"
                  onClick={onClick}
                  fullWidth={isMobile}
                  {...rest}
                >
                  {defaultLabel}
                </Button>
              );
            }}
          />
        )}
      </Grid>
    </PageGridContainer>
  );
};

export default EnterpriseLevelSetsModule;
